import { Navigate, Route, Routes } from 'react-router-dom';

import { Layout } from './components/Layout';
import { RequireAuth } from './auth/RequireAuth';

import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { VerifyEmail } from './pages/VerifyEmail';
import { AcceptInvite } from './pages/AcceptInvite';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { JoinPage } from './pages/JoinPage';
import { PostSurveyPage } from './pages/PostSurveyPage';
import { SetupTotp } from './pages/SetupTotp';
import { AccessPending } from './pages/AccessPending';
import { Access } from './pages/admin/Access';
import { Dashboard } from './pages/Dashboard';
import { Profile } from './pages/Profile';
import { TenantList } from './pages/admin/TenantList';
import { TenantDetail } from './pages/admin/TenantDetail';
import { Licenses } from './pages/admin/Licenses';
import { ViewPlatformAdmin } from './pages/admin/ViewPlatformAdmin';
import { Diagnostics } from './pages/admin/Diagnostics';
import { Configuration } from './pages/admin/Configuration';
import { Operations } from './pages/admin/Operations';
import { TenantDataIntegrity } from './pages/tenant/TenantDataIntegrity';
import { Baselines } from './pages/admin/Baselines';
import { LiveRecording } from './pages/LiveRecording';
import { TenantActivity } from './pages/tenant/TenantActivity';
import { TenantFocus } from './pages/tenant/TenantFocus';
import { TenantAppliances } from './pages/tenant/TenantAppliances';
import { TenantDevices } from './pages/tenant/TenantDevices';
import { TenantParticipants } from './pages/tenant/TenantParticipants';
import { TenantProceduresPage } from './pages/tenant/TenantProceduresPage';
import { TenantOrganization } from './pages/tenant/TenantOrganization';
import { TenantProcedureInstanceDetail } from './pages/tenant/TenantProcedureInstanceDetail';
import { TenantSettings } from './pages/tenant/TenantSettings';
import { HelpLayout } from './pages/help/HelpLayout';
import { GettingStarted } from './pages/help/GettingStarted';
import { Faq } from './pages/help/Faq';
import { ReleaseNotes } from './pages/help/ReleaseNotes';
import { About } from './pages/About';
import { LegalPage } from './pages/LegalPage';

/**
 * Route map. Two top-level zones:
 *
 *   - Unauthed pages (login, register, verify-email, accept-invite)
 *     render without the app shell. They're the "before you've
 *     signed in" pages, and most of them are landed on directly
 *     from emails.
 *
 *   - Authed pages render inside <Layout>, which mounts the sidebar,
 *     topbar (with dark-mode toggle + profile menu), and the routed
 *     content via <Outlet>.
 */
export default function App() {
  return (
    <Routes>
      {/* Unauthed */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public: a participant enrolling on their own phone from the booth
          kiosk's QR code (BOOTH-SELF-ENROL); the single-use code is the key. */}
      <Route path="/join/:code" element={<JoinPage />} />
      <Route path="/post-survey/:code" element={<PostSurveyPage />} />
      {/* Public: Terms of Use and Privacy Policy (LEGAL-TERMS). */}
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      {/* Standalone (no app shell): a signed-in but roleless user sits
          here until a superadmin grants access. */}
      <Route path="/pending" element={<AccessPending />} />

      {/* Authed app shell */}
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        {/* TOTP enrollment lives inside the authed shell (you have
            to be signed in to enroll). RequireAuth's TOTP gate lets
            this path through even when totp_enabled_at is null —
            otherwise it would loop. */}
        <Route path="/setup-2fa" element={<SetupTotp />} />

        <Route
          path="/admin/access"
          element={
            <RequireAuth role="superadmin">
              <Access />
            </RequireAuth>
          }
        />

        {/* Legacy access routes → the consolidated tabbed page. The
            platform-admin detail route (/:userId) stays as its own page. */}
        <Route
          path="/admin/platform-admins"
          element={<Navigate to="/admin/access?tab=admins" replace />}
        />
        <Route
          path="/admin/access-requests"
          element={<Navigate to="/admin/access?tab=requests" replace />}
        />
        <Route
          path="/admin/tenants"
          element={
            <RequireAuth role="superadmin">
              <TenantList />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/licenses"
          element={
            <RequireAuth role="superadmin">
              <Licenses />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/tenants/:tenantId"
          element={
            <RequireAuth role="superadmin">
              <TenantDetail />
            </RequireAuth>
          }
        />

        <Route
          path="/admin/platform-admins/:userId"
          element={
            <RequireAuth role="superadmin">
              <ViewPlatformAdmin />
            </RequireAuth>
          }
        />

        <Route
          path="/admin/diagnostics"
          element={
            <RequireAuth role="superadmin">
              <Diagnostics />
            </RequireAuth>
          }
        />

        {/* Legacy diagnostics routes → the consolidated tabbed page. */}
        <Route
          path="/admin/crash-reports"
          element={<Navigate to="/admin/diagnostics?tab=crashes" replace />}
        />
        <Route
          path="/admin/logs"
          element={<Navigate to="/admin/diagnostics?tab=logs" replace />}
        />
        <Route
          path="/admin/audit"
          element={<Navigate to="/admin/diagnostics?tab=audit" replace />}
        />

        <Route
          path="/admin/configuration"
          element={
            <RequireAuth role="superadmin">
              <Configuration />
            </RequireAuth>
          }
        />

        {/* Legacy config routes → the consolidated tabbed page (deep-link to
            the matching tab so old bookmarks still land in the right place). */}
        <Route
          path="/admin/mail"
          element={<Navigate to="/admin/configuration?tab=mail" replace />}
        />
        <Route
          path="/admin/insights"
          element={<Navigate to="/admin/configuration?tab=insights" replace />}
        />
        <Route
          path="/admin/mfa-policy"
          element={<Navigate to="/admin/configuration?tab=mfa" replace />}
        />

        <Route
          path="/admin/operations"
          element={
            <RequireAuth role="superadmin">
              <Operations />
            </RequireAuth>
          }
        />

        {/* Live Recording — near-real-time multi-participant overlay per case
            (LIVE-OR-STREAMING). */}
        <Route
          path="/live-wall"
          element={
            <RequireAuth role={['superadmin', 'tenant_admin', 'coordinator', 'participant']}>
              <LiveRecording />
            </RequireAuth>
          }
        />

        {/* Legacy operations routes → the consolidated tabbed page. */}
        <Route
          path="/admin/system-status"
          element={<Navigate to="/admin/operations?tab=status" replace />}
        />
        <Route
          path="/admin/workers"
          element={<Navigate to="/admin/operations?tab=workers" replace />}
        />
        <Route
          path="/admin/capture-app-releases"
          element={<Navigate to="/admin/operations?tab=releases" replace />}
        />
        <Route
          path="/admin/baselines"
          element={
            <RequireAuth role="superadmin">
              <Baselines />
            </RequireAuth>
          }
        />

        {/* Legacy baseline routes → the consolidated tabbed page. */}
        <Route
          path="/admin/master-baselines"
          element={<Navigate to="/admin/baselines?tab=master" replace />}
        />
        <Route
          path="/admin/master-baseline-submissions"
          element={
            <Navigate to="/admin/baselines?tab=submissions" replace />
          }
        />

        {/* Tenant admin pages. tenant_admin role required; the
            user's first active tenant_admin membership picks the
            implicit tenant. Multi-tenant admins land on the first
            one until we add a topbar switcher. */}
        <Route
          path="/tenant/participants"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator']}>
              <TenantParticipants />
            </RequireAuth>
          }
        />
        <Route
          path="/tenant/settings"
          element={
            <RequireAuth role="tenant_admin">
              <TenantSettings />
            </RequireAuth>
          }
        />
        <Route
          path="/tenant/procedures"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator']}>
              <TenantProceduresPage />
            </RequireAuth>
          }
        />
        <Route
          path="/tenant/organization"
          element={
            <RequireAuth role="tenant_admin">
              <TenantOrganization />
            </RequireAuth>
          }
        />
        {/* Folded into the combined Procedures page (Instances tab). */}
        <Route
          path="/tenant/procedure-instances"
          element={
            <Navigate to="/tenant/procedures?tab=instances" replace />
          }
        />
        <Route
          path="/tenant/procedure-instances/:id"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator']}>
              <TenantProcedureInstanceDetail />
            </RequireAuth>
          }
        />
        {/* Department analytics route temporarily disabled — see the
            note in Sidebar.tsx. Page + endpoint remain; re-add this
            Route + the sidebar entry to re-enable. */}
        <Route
          path="/tenant/devices"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator']}>
              <TenantDevices />
            </RequireAuth>
          }
        />
        <Route
          path="/tenant/data-integrity"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator']}>
              <TenantDataIntegrity />
            </RequireAuth>
          }
        />
        <Route
          path="/tenant/appliances"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator']}>
              <TenantAppliances />
            </RequireAuth>
          }
        />
        {/* Folded into Settings as a tab. */}
        <Route
          path="/tenant/cohort-baseline"
          element={<Navigate to="/tenant/settings?tab=cohort" replace />}
        />
        <Route
          path="/tenant/activity"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator']}>
              <TenantActivity />
            </RequireAuth>
          }
        />
        <Route
          path="/tenant/analysis"
          element={
            <RequireAuth role={['tenant_admin', 'coordinator', 'participant']}>
              <TenantFocus />
            </RequireAuth>
          }
        />
        {/* Former name. */}
        <Route path="/tenant/focus" element={<Navigate to="/tenant/analysis" replace />} />
        {/* Folded into Settings as tabs. */}
        <Route
          path="/tenant/messages"
          element={<Navigate to="/tenant/settings?tab=messages" replace />}
        />
        <Route
          path="/tenant/admins"
          element={<Navigate to="/tenant/settings?tab=admins" replace />}
        />
        {/* Legacy URLs from the prior split-list view. Bookmarks +
            docs that pointed at /tenant/sessions or /tenant/baselines
            land on the unified Activity page so nothing 404s. */}
        <Route
          path="/tenant/sessions"
          element={<Navigate to="/tenant/activity" replace />}
        />
        <Route
          path="/tenant/baselines"
          element={<Navigate to="/tenant/activity" replace />}
        />

        <Route path="/help" element={<HelpLayout />}>
          <Route index element={<GettingStarted />} />
          <Route path="faq" element={<Faq />} />
          <Route path="release-notes" element={<ReleaseNotes />} />
        </Route>
        {/* About moved out of the Help section on 2026-06-11 — it's
            now a top-level sidebar entry visible to every role. The
            legacy /help/about path redirects so old bookmarks land. */}
        <Route path="/about" element={<About />} />
        <Route
          path="/help/about"
          element={<Navigate to="/about" replace />}
        />
      </Route>

      {/* Any unmatched path: send to the post-login landing. The
          RequireAuth wrapping /dashboard will bounce unauthed users
          to /login which is the natural fall-through. */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
